import { Injectable } from '@angular/core';
import { identity, pickBy } from 'lodash-es';
import { Observable, of, throwError } from 'rxjs';
import { map, mapTo, switchMap } from 'rxjs/operators';

import { ClientService } from '../../client.service';
import { GetResponse, ListResponse } from '../../models';

import {
  CreateInvitationRequest,
  CreateInvitationToTransferOwnershipRequest,
  Invitation,
  InvitationCompanyDetails,
  InvitationInstallationDetails,
  InvitationOwnerDetails,
  InvitationsQueryParams,
  InvitationStatusType,
  UpdateInvitationRequest,
} from './invitations.model';

@Injectable()
export class InvitationsApi {
  private readonly version = 'v1';

  constructor(private clientService: ClientService) {}

  /**
   * List invitations
   * @param params
   * @param version
   */
  listInvitations(
    params: InvitationsQueryParams = {},
    version: string = 'v1'
  ): Observable<ListResponse<Invitation>> {
    const endpoint = `iot/${version}/user-management/invitations`;

    return this.clientService.get(endpoint, pickBy(params, identity));
  }

  /**
   * Gets details of invitation
   * @param token
   * @param version
   */
  getInvitation(
    token: string,
    version: string = this.version
  ): Observable<GetResponse<Invitation>> {
    const endpoint = `iot/${version}/user-management/invitations/${token}`;

    return this.clientService.get(endpoint);
  }

  /**
   * Get details of installation to which a user is invited
   * @param token
   * @param version
   */
  getInvitationInstallationDetails(
    token: string,
    version: string = this.version
  ): Observable<GetResponse<InvitationInstallationDetails>> {
    const endpoint = `iot/${version}/user-management/invitations/${token}/installationDetails`;

    return this.clientService.get(endpoint);
  }

  /**
   * Get details about inviter account
   * @param token
   * @param version
   */
  getInvitationOwnerDetails(
    token: string,
    version: string = this.version
  ): Observable<GetResponse<InvitationOwnerDetails>> {
    const endpoint = `iot/${version}/user-management/invitations/${token}/ownerDetails`;

    return this.clientService.get(endpoint);
  }

  /**
   * Get company details of invited company
   * @param token
   * @param version
   */
  getInvitationCompanyDetails(
    token: string,
    version?: string
  ): Observable<{ entities: any; links: any; properties: InvitationCompanyDetails }> {
    const ver = this.clientService.getVersion(version);
    const endpoint = `service/${ver}/invitations/${token}/company-details`;

    return this.clientService.get(endpoint);
  }

  /**
   * Create an invitation
   * @param payload
   * @param version
   */
  createInvitation(
    payload: CreateInvitationRequest,
    version: string = this.version
  ): Observable<GetResponse<Invitation>> {
    const endpoint = `iot/${version}/user-management/invitations`;

    return this.clientService.post(endpoint, payload);
  }

  /**
   * Create an invitation to transfer ownership from maintainer to end user (gateway commissioning)
   * @param payload
   * @param version
   */
  createInvitationToTransferOwnership(
    payload: CreateInvitationToTransferOwnershipRequest,
    version: string = this.version
  ): Observable<{ data: Invitation }> {
    const endpoint = `iot/${version}/user-management/invitations`;

    return this.clientService.post(endpoint, payload);
  }

  /**
   * Update the status of an invitation
   * @param token
   * @param payload
   * @param version
   */
  updateInvitation(
    token: string,
    payload: UpdateInvitationRequest,
    version: string = this.version
  ): Observable<{ data: Invitation }> {
    const endpoint = `iot/${version}/user-management/invitations/${token}`;

    return this.clientService.post(endpoint, payload);
  }

  /**
   * Update visibility of an invitation
   * @param token
   * @param version
   */
  updateInvitationVisibility(
    token: string,
    isVisible: boolean,
    version: string = this.version
  ): Observable<{ data: Invitation }> {
    const endpoint = `iot/${version}/user-management/invitations/${token}`;

    return this.clientService.put(endpoint, { token, isVisible });
  }

  /**
   * Stop monitoring installation by terminate the inviatation
   * @param installationId
   * @param email
   * @param version
   */
  unfollowInstallation(
    installationId: number,
    email: string,
    version: string = this.version
  ): Observable<{}> {
    return this.listInvitations(
      { status: InvitationStatusType.Accepted, installationId },
      version
    ).pipe(
      map((response) => {
        const invitation = response?.data?.find((inv) => inv.inviteeEmail === email);

        return invitation ? invitation.token : response?.data[0]?.token;
      }),
      switchMap((token) => {
        if (token) {
          return this.updateInvitation(token, { status: InvitationStatusType.Terminated }).pipe(
            mapTo({})
          );
        }

        return throwError(() => of({}));
      })
    );
  }
}
